<template>
  <div class="sidebar-left">
    <div class="sidebar">
      <div
        class="sidebar-content customer-chat-sidebar"
        :class="{ show: mqShallShowLeftSidebar }"
      >
        <span class="sidebar-close-icon">
          <feather-icon
            icon="XIcon"
            size="16"
            @click="$emit('update:mq-shall-show-left-sidebar', false)"
          />
        </span>

        <div class="chat-fixed-search">
          <div class="d-flex align-items-center w-100">
            <b-input-group class="input-group-merge w-100 round">
              <b-input-group-prepend is-text>
                <feather-icon
                  icon="SearchIcon"
                  class="text-muted"
                />
              </b-input-group-prepend>
              <b-form-input
                v-model="searchQuery"
                placeholder="Buscar cliente..."
              />
            </b-input-group>
          </div>
        </div>

        <div class="chat-user-list-wrapper list-group customer-chat-list-wrapper">
          <b-nav
            tabs
            fill
            class="customer-chat-tabs mb-1"
          >
            <b-nav-item
              v-for="tab in tabs"
              :key="tab.scope"
              :active="activeScope === tab.scope"
              @click="setScope(tab.scope)"
            >
              {{ tab.label }}
              <b-badge
                v-if="unreadByScope[tab.scope]"
                pill
                variant="danger"
                class="ml-25"
              >
                {{ unreadByScope[tab.scope] }}
              </b-badge>
            </b-nav-item>
          </b-nav>

          <div
            v-if="inbox.loading && !inbox.loaded"
            class="d-flex justify-content-center p-2"
          >
            <b-spinner
              small
              variant="primary"
            />
          </div>

          <div
            v-else-if="inbox.error && !items.length"
            class="p-2 text-center"
          >
            <p class="text-muted mb-1">
              Não foi possível carregar as conversas.
            </p>
            <b-button
              size="sm"
              variant="outline-primary"
              @click="reload"
            >
              Tentar novamente
            </b-button>
          </div>

          <p
            v-else-if="!filteredItems.length"
            class="text-muted text-center p-2 mb-0"
          >
            {{ searchQuery ? 'Nenhuma conversa encontrada.' : emptyText }}
          </p>

          <ul
            v-else
            class="chat-users-list chat-list media-list"
          >
            <li
              v-for="conversation in filteredItems"
              :key="conversation.conversationId"
              :class="{ active: conversation.conversationId === activeConversationId }"
              @click="$emit('open-conversation', conversation.conversationId)"
            >
              <b-avatar
                size="42"
                variant="light-primary"
                :text="avatarText(conversation)"
              />
              <div class="chat-info flex-grow-1">
                <h5 class="mb-0 text-truncate">
                  {{ displayName(conversation) }}
                </h5>
                <p class="card-text text-truncate mb-0">
                  {{ subtitle(conversation) }}
                </p>
              </div>
              <!-- Coluna: horário em cima, contador/status embaixo (lado a lado com
                   float-right o badge colava no horário). -->
              <div class="chat-meta text-nowrap d-flex flex-column align-items-end">
                <small class="mb-50 chat-time">
                  {{ formatInboxDate(conversation.lastMessageAt) }}
                </small>
                <b-badge
                  v-if="conversation.unreadCount"
                  pill
                  variant="primary"
                >
                  {{ conversation.unreadCount }}
                </b-badge>
                <b-badge
                  v-else-if="statusLabel(conversation)"
                  pill
                  variant="light-secondary"
                >
                  {{ statusLabel(conversation) }}
                </b-badge>
              </div>
            </li>
          </ul>

          <div
            v-if="inbox.hasMore && !searchQuery"
            class="p-1 text-center"
          >
            <b-button
              size="sm"
              variant="flat-primary"
              :disabled="inbox.loading"
              @click="loadMore"
            >
              <b-spinner
                v-if="inbox.loading"
                small
              />
              Carregar mais
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BNav,
  BNavItem,
  BSpinner,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import * as types from '../store/types'
import { INBOX_SCOPE_OWNED, INBOX_SCOPE_INVITED, inboxSubtitle } from '../utils/inbox'
import { formatInboxDate, initials } from '../utils/format'

const STATUS_LABELS = {
  ARCHIVED: 'Arquivada',
  BLOCKED: 'Bloqueada',
}

export default {
  components: {
    BAvatar,
    BBadge,
    BButton,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BNav,
    BNavItem,
    BSpinner,
  },
  props: {
    activeConversationId: {
      type: String,
      default: null,
    },
    mqShallShowLeftSidebar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      tabs: [
        { scope: INBOX_SCOPE_OWNED, label: 'Minhas conversas' },
        { scope: INBOX_SCOPE_INVITED, label: 'Direcionadas a mim' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      activeScope: types.ACTIVE_SCOPE,
      inboxByScope: types.INBOX,
      itemsByScope: types.INBOX_ITEMS,
    }),
    inbox() {
      return this.inboxByScope(this.activeScope) || {}
    },
    items() {
      return this.itemsByScope(this.activeScope)
    },
    filteredItems() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return this.items
      return this.items.filter(item => this.displayName(item).toLowerCase().includes(query))
    },
    unreadByScope() {
      return this.tabs.reduce((acc, tab) => {
        acc[tab.scope] = this.itemsByScope(tab.scope)
          .reduce((total, item) => total + (Number(item.unreadCount) || 0), 0)
        return acc
      }, {})
    },
    emptyText() {
      return this.activeScope === INBOX_SCOPE_INVITED
        ? 'Nenhuma conversa direcionada a você.'
        : 'Nenhuma conversa por aqui ainda.'
    },
  },
  methods: {
    formatInboxDate,
    setScope(scope) {
      this.$store.dispatch(types.SET_ACTIVE_SCOPE, scope)
    },
    reload() {
      this.$store.dispatch(types.FETCH_INBOX, { scope: this.activeScope, page: 0 }).catch(() => {})
    },
    loadMore() {
      this.$store.dispatch(types.FETCH_INBOX, { scope: this.activeScope, page: (this.inbox.page || 0) + 1 })
        .catch(() => {})
    },
    displayName(conversation) {
      return conversation.customerDisplayName || 'Cliente'
    },
    avatarText(conversation) {
      return initials(this.displayName(conversation))
    },
    subtitle(conversation) {
      return inboxSubtitle(conversation)
    },
    statusLabel(conversation) {
      return STATUS_LABELS[conversation.status] || null
    },
  },
}
</script>
